'use client';

import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const monthlyData = [
  { month: 'Mar', earnings: 1200000, bookings: 5 },
  { month: 'Apr', earnings: 1850000, bookings: 7 },
  { month: 'May', earnings: 2100000, bookings: 9 },
  { month: 'Jun', earnings: 1750000, bookings: 6 },
  { month: 'Jul', earnings: 2400000, bookings: 10 },
  { month: 'Aug', earnings: 3100000, bookings: 13 },
];

const propertyBreakdown = [
  { name: 'Lakeside Villa', earnings: 1800000, pct: 58 },
  { name: 'Kampala Apt', earnings: 570000, pct: 18 },
  { name: 'Bwindi Lodge', earnings: 500000, pct: 16 },
  { name: 'Jinja Cottage', earnings: 230000, pct: 8 },
];

const formatUGX = (v: number) => `UGX ${(v / 1000000).toFixed(1)}M`;

export default function Earnings() {
  const [period, setPeriod] = useState<'6m' | '3m' | '1m'>('6m');

  const data = period === '1m' ? monthlyData.slice(-1) : period === '3m' ? monthlyData.slice(-3) : monthlyData;

  return (
    <div>
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Earnings', value: 'UGX 12.4M', sub: 'All time', trend: '+18%' },
          { label: 'This Month', value: 'UGX 3.1M', sub: 'August 2026', trend: '+29%' },
          { label: 'Pending Payout', value: 'UGX 1.8M', sub: 'Processing', trend: null },
          { label: 'Avg Per Night', value: 'UGX 185K', sub: 'Across listings', trend: '+5%' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <div className="text-xl font-bold text-gray-900">{s.value}</div>
            <div className="text-sm font-semibold text-gray-700 mt-0.5">{s.label}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-gray-400">{s.sub}</span>
              {s.trend && <span className="text-xs font-bold text-emerald-600">{s.trend}</span>}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Bar chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Monthly Earnings</h3>
            <div className="flex gap-1">
              {(['1m', '3m', '6m'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                    period === p ? 'bg-rose-500 text-white' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v: number) => [formatUGX(v), 'Earnings']} contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
              <Bar dataKey="earnings" fill="#f43f5e" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Property breakdown */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-4">By Property</h3>
          <div className="space-y-4">
            {propertyBreakdown.map((p) => (
              <div key={p.name}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-semibold text-gray-700 truncate pr-2">{p.name}</span>
                  <span className="text-gray-500 text-xs whitespace-nowrap">UGX {(p.earnings / 1000).toFixed(0)}K</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-rose-400 rounded-full transition-all duration-700"
                    style={{ width: `${p.pct}%` }}
                  />
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{p.pct}% of total</div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="text-xs text-gray-500 mb-1">Bookings trend</div>
            <ResponsiveContainer width="100%" height={60}>
              <LineChart data={monthlyData}>
                <Line type="monotone" dataKey="bookings" stroke="#f43f5e" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent transactions */}
      <div className="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="font-bold text-gray-900 mb-4">Recent Transactions</h3>
        <div className="space-y-3">
          {[
            { desc: 'Booking — Lakeside Villa (5 nights)', date: 'Aug 20, 2026', amount: 900000, type: 'credit' },
            { desc: 'Booking — Bwindi Lodge (4 nights)', date: 'Aug 15, 2026', amount: 1000000, type: 'credit' },
            { desc: 'Platform fee (3%)', date: 'Aug 15, 2026', amount: 30000, type: 'debit' },
            { desc: 'Booking — Kampala Apt (3 nights)', date: 'Aug 10, 2026', amount: 285000, type: 'credit' },
            { desc: 'Payout to MTN Mobile Money', date: 'Aug 05, 2026', amount: 1500000, type: 'payout' },
          ].map((t, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
              <div>
                <div className="text-sm font-semibold text-gray-800">{t.desc}</div>
                <div className="text-xs text-gray-400">{t.date}</div>
              </div>
              <div className={`text-sm font-bold ${t.type === 'credit' ? 'text-emerald-600' : t.type === 'debit' ? 'text-red-500' : 'text-gray-500'}`}>
                {t.type === 'credit' ? '+' : '-'}UGX {t.amount.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
